import { useState, useCallback, useEffect } from 'react'
import { PageHeader, DataTable, ColumnSettingsButton, btnPrimary, btnDanger, colors } from '../../components/ui'
import type { Column } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { AgentTokenModal, PlaintextRevealModal } from './AgentTokenModal'
import { relativeTime } from '../fleet/fleet-helpers'
import { useTableColumns } from '../../hooks/use-table-columns'

interface AgentTokenSummary {
  readonly id: string
  readonly name: string
  readonly scope: readonly string[]
  readonly allowedEnvironmentIds: readonly string[] | null
  readonly allowedLogLevels: readonly string[]
  readonly allowedEvtPatterns: readonly string[]
  readonly maxWindowHours: number
  readonly rateLimitPerMinute: number
  readonly createdAt: string
  readonly expiresAt: string
  readonly revokedAt: string | null
  readonly lastUsedAt: string | null
}

interface Environment {
  readonly id: string
  readonly name: string
}

function formatDate(dt: string | null): string {
  if (!dt) return '—'
  try {
    return new Date(dt).toLocaleDateString()
  } catch {
    return dt
  }
}

function StatusPill({ revoked, expired }: { revoked: boolean; expired: boolean }) {
  const bg = revoked ? '#fca5a5' : expired ? '#fde68a' : '#bbf7d0'
  const color = revoked ? '#7f1d1d' : expired ? '#92400e' : '#14532d'
  const label = revoked ? 'Revoked' : expired ? 'Expired' : 'Active'
  return (
    <span
      style={{
        display: 'inline-block',
        background: bg,
        color,
        borderRadius: '9999px',
        padding: '2px 10px',
        fontSize: '0.75rem',
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  )
}

export function AgentTokensPage() {
  const [tokens, setTokens] = useState<readonly AgentTokenSummary[]>([])
  const [environments, setEnvironments] = useState<readonly Environment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [plaintextInfo, setPlaintextInfo] = useState<{ plaintext: string; tokenName: string } | null>(null)
  const [revoking, setRevoking] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const { tableId: _, ...tableColumnProps } = useTableColumns('agent-tokens')

  const centralUrl = window.location.origin

  useEffect(() => {
    let cancelled = false
    Promise.all([
      api.get<readonly AgentTokenSummary[]>('/admin/agent-tokens'),
      api.get<readonly Environment[]>('/fleet/environments'),
    ])
      .then(([tokensRes, envsRes]) => {
        if (cancelled) return
        setTokens(tokensRes.data ?? [])
        setEnvironments(envsRes.data ?? [])
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load tokens')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [refreshKey])

  const handleRevoke = useCallback((id: string) => {
    setRevoking(id)
    api
      .post<void>(`/admin/agent-tokens/${id}/revoke`, {})
      .then(() => {
        setRevoking(null)
        setRefreshKey((k) => k + 1)
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'Failed to revoke token')
        setRevoking(null)
      })
  }, [])

  const handleCreated = useCallback((plaintext: string, tokenName: string) => {
    setShowCreateModal(false)
    setPlaintextInfo({ plaintext, tokenName })
    setRefreshKey((k) => k + 1)
  }, [])

  const columns: readonly Column<AgentTokenSummary>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (r) => <strong style={{ fontSize: '0.875rem' }}>{r.name}</strong>,
      sortable: true,
    },
    {
      key: 'scope',
      header: 'Scope',
      render: (r) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {(r.scope as string[]).map((s) => (
            <code
              key={s}
              style={{
                background: '#eff6ff',
                color: '#1d4ed8',
                padding: '1px 6px',
                borderRadius: '4px',
                fontSize: '0.7rem',
              }}
            >
              {s}
            </code>
          ))}
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (r) => formatDate(r.createdAt),
      sortable: true,
    },
    {
      key: 'expiresAt',
      header: 'Expires',
      render: (r) => formatDate(r.expiresAt),
      sortable: true,
    },
    {
      key: 'lastUsedAt',
      header: 'Last Used',
      render: (r) => (r.lastUsedAt ? relativeTime(r.lastUsedAt) : '—'),
      sortable: true,
    },
    {
      key: 'revokedAt',
      header: 'Status',
      render: (r) => (
        <StatusPill revoked={!!r.revokedAt} expired={!r.revokedAt && new Date(r.expiresAt) < new Date()} />
      ),
    },
    {
      key: 'id',
      header: '',
      render: (r) => {
        const isRevoked = !!r.revokedAt
        const isExpired = !isRevoked && new Date(r.expiresAt) < new Date()
        const canRevoke = !isRevoked && !isExpired
        return (
          <button
            style={{ ...btnDanger, fontSize: '0.75rem', padding: '3px 10px', opacity: canRevoke ? 1 : 0.4 }}
            disabled={!canRevoke || revoking === r.id}
            onClick={(e) => {
              e.stopPropagation()
              handleRevoke(r.id)
            }}
          >
            {revoking === r.id ? 'Revoking…' : 'Revoke'}
          </button>
        )
      },
    },
  ]

  return (
    <div style={{ padding: '1.5rem' }}>
      <PageHeader
        title="Agent Tokens"
        subtitle="Issue scoped API tokens for LLM-agent access to the fleet observability surface"
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button style={btnPrimary} onClick={() => setShowCreateModal(true)}>
              Create Token
            </button>
          </>
        }
      />

      {error && (
        <div
          style={{
            background: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: '6px',
            padding: '0.625rem 0.875rem',
            marginBottom: '1rem',
            fontSize: '0.875rem',
            color: '#991b1b',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>Loading…</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={tokens as AgentTokenSummary[]}
            keyField="id"
            emptyMessage="No agent tokens yet — create one to enable LLM-agent access"
            onRowClick={(r) => setExpandedId((prev) => (prev === r.id ? null : r.id))}
            {...tableColumnProps}
          />

          {/* Row expansion panel for selected token */}
          {expandedId &&
            (() => {
              const token = tokens.find((t) => t.id === expandedId)
              if (!token) return null
              const envList = (token.allowedEnvironmentIds as string[] | null) ?? null
              const curlSnippet = `curl -H "Authorization: Bearer <token>" ${centralUrl}/api/agent/logs?level=error`
              const mcpConfig = JSON.stringify(
                {
                  mcpServers: {
                    'alphaedge-fleet': {
                      command: 'npx',
                      args: ['mcp-remote', `${centralUrl}/api/agent/mcp-tools.json`],
                      env: { MCP_AUTH_TOKEN: '<token>' },
                    },
                  },
                },
                null,
                2,
              )
              return (
                <div
                  style={{
                    marginTop: '-1px',
                    padding: '1rem 1.25rem',
                    background: '#f8fafc',
                    border: `1px solid ${colors.border}`,
                    borderTop: 'none',
                    borderRadius: '0 0 8px 8px',
                    fontSize: '0.8125rem',
                  }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '0.75rem',
                      marginBottom: '0.75rem',
                    }}
                  >
                    <div>
                      <strong>Allowed Envs:</strong>{' '}
                      {envList
                        ? envList
                            .map((id) => {
                              const env = environments.find((e) => e.id === id)
                              return env ? env.name : id
                            })
                            .join(', ')
                        : 'All environments'}
                    </div>
                    <div>
                      <strong>Log Levels:</strong> {(token.allowedLogLevels as string[]).join(', ')}
                    </div>
                    <div>
                      <strong>Evt Patterns:</strong> {(token.allowedEvtPatterns as string[]).join(', ')}
                    </div>
                    <div>
                      <strong>Max Window:</strong> {token.maxWindowHours}h
                    </div>
                    <div>
                      <strong>Rate Limit:</strong> {token.rateLimitPerMinute}/min
                    </div>
                  </div>

                  <details style={{ marginBottom: '0.5rem' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600, color: colors.textMuted }}>
                      curl snippet
                    </summary>
                    <pre
                      style={{
                        background: '#1e293b',
                        color: '#e2e8f0',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        padding: '0.625rem',
                        borderRadius: '6px',
                        marginTop: '0.5rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                      }}
                    >
                      {curlSnippet}
                    </pre>
                  </details>

                  <details>
                    <summary style={{ cursor: 'pointer', fontWeight: 600, color: colors.textMuted }}>
                      MCP config block
                    </summary>
                    <pre
                      style={{
                        background: '#1e293b',
                        color: '#e2e8f0',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        padding: '0.625rem',
                        borderRadius: '6px',
                        marginTop: '0.5rem',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {mcpConfig}
                    </pre>
                  </details>
                </div>
              )
            })()}
        </>
      )}

      {showCreateModal && (
        <AgentTokenModal
          environments={environments as Environment[]}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}

      {plaintextInfo && (
        <PlaintextRevealModal
          plaintext={plaintextInfo.plaintext}
          tokenName={plaintextInfo.tokenName}
          onClose={() => setPlaintextInfo(null)}
        />
      )}
    </div>
  )
}
